<template>
    <div>
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && starProduct.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="starProduct"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="starProduct"
                            :featured-image="starProduct.featuredImageId"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-featured-image-change="onFeaturedImageChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';
import AddEditForm from '@/components/star-products/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage Star Product'
    },

    validations: {
        starProduct: {
            name: {
                required
            },
            description: {
                required
            },
            stars: {
                required,
                hasToBePositiveNumber: v => {
                    v = parseInt(v);

                    if (isNaN(v)) {
                        return false;
                    }

                    return v > 0;
                }
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit star product' : 'Add star product',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Star Products',
                    to: '/star-products'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            starProduct: {},
            serverErrors: []
        };
    },

    computed: {
        sections() {
            const sections = {
                featuredImage: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.starProduct = await this.getStarProduct(this.id);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addStarProduct: 'starProducts/store',
            deleteStarProduct: 'starProducts/destroy',
            getStarProduct: 'starProducts/show',
            updateStarProduct: 'starProducts/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addStarProduct(this.starProduct);

                    this.$router.push(`/star-products/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateStarProduct({
                        id: this.id,
                        data: this.starProduct
                    });

                    this.starProduct.updatedAt = updatedAt;

                    this.$toaster('Star product updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteStarProduct(this.id);

                this.$router.push('/star-products');
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        onFeaturedImageChange(imageId) {
            this.starProduct.featuredImageId = imageId;
        }
    }
};
</script>
